import { AxiosInstance, AxiosResponse } from 'axios';
import {
  ValveCommand,
  ValveCommandResponse,
  ValveNodeListResponse,
  ValveNodeResponse
} from '@/types/valvenodes';
import { Device, Valve as ApiValve } from '@/types/api';

class ValveNodeApiService {
  private api: AxiosInstance;

  constructor(apiInstance: AxiosInstance) {
    this.api = apiInstance;
  }

  /**
   * Get all valve nodes
   */
  async getValveNodes(): Promise<AxiosResponse<ValveNodeListResponse>> {
    // For now, we'll use the existing devices endpoint and transform the data
    // In the future, this could be a dedicated valve nodes endpoint
    const [devicesResponse, valvesResponse] = await Promise.all([
      this.api.get<Device[]>('/devices/'),
      this.api.get<ApiValve[]>('/valves')
    ]);
    
    // Transform devices to valve nodes with valve data
    const { transformDevicesToValveNodes } = await import('@/types/valvenodes/transformers');
    const valveNodes = transformDevicesToValveNodes(devicesResponse.data, { valves: valvesResponse.data });
    
    const valveNodesResponse: ValveNodeListResponse = {
      nodes: valveNodes,
      total: valveNodes.length,
      page: 1,
      limit: valveNodes.length,
    };
    
    return {
      ...devicesResponse,
      data: valveNodesResponse,
    };
  }

  /**
   * Get a single valve node by ID
   */
  async getValveNode(nodeId: string): Promise<AxiosResponse<ValveNodeResponse>> {
    // For now, we'll use the existing devices endpoint and transform the data
    const response = await this.api.get<Device>(`/devices/${nodeId}`);
    
    // Transform device to valve node
    const { transformDeviceToValveNode } = await import('@/types/valvenodes/transformers');
    const valveNode = transformDeviceToValveNode(response.data);
    
    const valveNodeResponse: ValveNodeResponse = {
      node: valveNode,
    };
    
    return {
      ...response,
      data: valveNodeResponse,
    };
  }

  /**
   * Control a valve on a node
   */
  async controlValve(nodeId: string, command: ValveCommand): Promise<AxiosResponse<ValveCommandResponse>> {
    // Use the existing device control endpoint
    const response = await this.api.post(`/devices/${nodeId}/control/valve`, {
      valve: command.valve,
      action: command.action,
      ...(command.timeControlSeconds && {
        time_control_seconds: command.timeControlSeconds
      }),
      ...(command.flowControlPulses && {
        flow_control_pulses: command.flowControlPulses
      }),
      ...(command.untilClosed && { until_closed: true }),
    });
    
    // Transform the response to match our expected format
    const valveCommandResponse: ValveCommandResponse = {
      success: true,
      message: response.data.message || 'Command sent successfully',
      sequence: response.data.sequence_used || 0,
      payload: response.data.payload_hex || '',
    };
    
    return {
      ...response,
      data: valveCommandResponse,
    };
  }

  /**
   * Get valve node metrics
   */
  async getValveNodeMetrics(nodeId: string): Promise<AxiosResponse<any>> {
    // This would be a new endpoint for detailed metrics
    // For now, we'll return the device data which includes basic metrics
    return this.api.get(`/devices/${nodeId}`);
  }

  /**
   * Get valve node history/logs
   */
  async getValveNodeHistory(
    nodeId: string,
    options: {
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<AxiosResponse<any>> {
    // This would be a new endpoint for historical data
    const params = new URLSearchParams();
    
    if (options.startDate) params.append('start_date', options.startDate);
    if (options.endDate) params.append('end_date', options.endDate);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    
    const queryString = params.toString();
    const url = `/devices/${nodeId}/history${queryString ? `?${queryString}` : ''}`;
    
    return this.api.get(url);
  }

  /**
   * Update valve node configuration
   */
  async updateValveNodeConfig(
    nodeId: string,
    config: {
      reportingInterval?: number;
      timezone?: number;
      name?: string;
      location?: string;
    }
  ): Promise<AxiosResponse<any>> {
    const updates: any = {};
    
    if (config.reportingInterval !== undefined) {
      updates.reporting_interval = config.reportingInterval;
    }
    
    if (config.timezone !== undefined) {
      updates.timezone_offset = config.timezone;
    }
    
    // Use existing device configuration endpoints
    const promises: Promise<AxiosResponse<any>>[] = [];
    
    if (config.reportingInterval !== undefined) {
      promises.push(this.api.post(`/devices/${nodeId}/config/reporting-interval`, {
        interval_seconds: config.reportingInterval
      }));
    }
    
    if (config.timezone !== undefined) {
      promises.push(this.api.post(`/devices/${nodeId}/config/timezone`, {
        timezone_offset: config.timezone
      }));
    }
    
    // Execute all configuration updates in parallel
    const responses = await Promise.all(promises);
    
    // Return the first response as the main response
    return responses[0];
  }

  /**
   * Sync time on valve node
   */
  async syncValveNodeTime(nodeId: string): Promise<AxiosResponse<any>> {
    return this.api.post(`/devices/${nodeId}/config/sync-time`);
  }

  /**
   * Get valve node errors/alerts
   */
  async getValveNodeErrors(nodeId: string): Promise<AxiosResponse<any>> {
    // This would be a new endpoint for errors/alerts
    // For now, we'll use the monitoring endpoint
    return this.api.get(`/monitoring/device/${nodeId}/errors`);
  }

  /**
   * Acknowledge/clear valve node errors
   */
  async acknowledgeValveNodeErrors(
    nodeId: string,
    errorIds: string[]
  ): Promise<AxiosResponse<any>> {
    // This would be a new endpoint for acknowledging errors
    return this.api.post(`/devices/${nodeId}/errors/acknowledge`, {
      error_ids: errorIds
    });
  }

  /**
   * Get valve node schedules
   */
  async getValveNodeSchedules(nodeId: string): Promise<AxiosResponse<any>> {
    // Use existing schedules endpoint
    return this.api.get(`/schedules?device_eui=${nodeId}`);
  }

  /**
   * Create valve node schedule
   */
  async createValveNodeSchedule(
    nodeId: string,
    schedule: any
  ): Promise<AxiosResponse<any>> {
    // Use existing schedules endpoint
    return this.api.post('/schedules', {
      ...schedule,
      device_eui: nodeId,
    });
  }

  /**
   * Update valve node schedule
   */
  async updateValveNodeSchedule(
    _nodeId: string,
    scheduleId: number,
    schedule: any
  ): Promise<AxiosResponse<any>> {
    // Use existing schedules endpoint
    return this.api.put(`/schedules/${scheduleId}`, schedule);
  }

  /**
   * Delete valve node schedule
   */
  async deleteValveNodeSchedule(
    _nodeId: string,
    scheduleId: number
  ): Promise<AxiosResponse<any>> {
    // Use existing schedules endpoint
    return this.api.delete(`/schedules/${scheduleId}`);
  }

  /**
   * Enable/disable valve node schedule
   */
  async toggleValveNodeSchedule(
    _nodeId: string,
    scheduleId: number,
    enabled: boolean
  ): Promise<AxiosResponse<any>> {
    // Use existing schedules endpoint
    const endpoint = enabled ? `/schedules/${scheduleId}/enable` : `/schedules/${scheduleId}/disable`;
    return this.api.post(endpoint);
  }
}

// Create a singleton instance that will be initialized with the main API service
let valveNodeApiService: ValveNodeApiService | null = null;

export const initializeValveNodeApi = (apiInstance: AxiosInstance): ValveNodeApiService => {
  if (!valveNodeApiService) {
    valveNodeApiService = new ValveNodeApiService(apiInstance);
  }
  return valveNodeApiService;
};

// Export a function to get the service instance
export const getValveNodeApi = (): ValveNodeApiService => {
  if (!valveNodeApiService) {
    throw new Error('ValveNodeApiService not initialized. Call initializeValveNodeApi first.');
  }
  return valveNodeApiService;
};

// Export the service class for testing
export { ValveNodeApiService };

// Default export for backward compatibility
export default valveNodeApiService;